import { RegionApi } from '@business/api/tm_region';
import { CategoryId } from '@business/entities/tm';
import { RegionId, TmRegionResponse } from '@business/entities/tm/TmRegionResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { CategoryRegionProviderPriorityForm } from '@modules/settings/SettingsPage/Components/BillingTab/Tabs/CategoryTab/ManageProviderPriorityOverlay/ManageProviderPriorityOverlay';
import { RegionFormData } from '@modules/settings/SettingsPage/Components/TimeMaterialsTab/AddEditRegionModal/RegionForm';
import { switchMap, tap } from 'rxjs/operators';

export class RegionBloc extends BaseBloc {
  private readonly tmRegionsRepo = new Repository<TmRegionResponse>({
    getItemId: term => term.id,
  });

  readonly regions$ = this.tmRegionsRepo.items$;
  readonly selectTemplate = this.tmRegionsRepo.selectItem;

  onReset() {
    this.tmRegionsRepo.reset();
  }

  fetchRegions = () => {
    return RegionApi.fetchRegions().pipe(this.tmRegionsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_region_error') }));
  };

  fetchCategoryServiceProviders = (categoryId: CategoryId) => {
    return RegionApi.fetchCategoryServiceProviders(categoryId).pipe(
      this.tmRegionsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_region_error') }),
    );
  };

  fetchRegion = (id: RegionId) => {
    return RegionApi.fetchRegion(id).pipe(
      this.tmRegionsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_region_error') }),
    );
  };

  createRegion = (data: RegionFormData) => {
    return RegionApi.createRegion(data).pipe(
      this.tmRegionsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_region'),
      }),
    );
  };

  updateRegion = (id: RegionId, data: RegionFormData) => {
    return RegionApi.updateRegion(id, data).pipe(
      this.tmRegionsRepo.ops.upsertOne(item => ({ item })),
      tap(() => this.fetchRegions().subscribe()),
      handleMessage({
        type: requestMessage('update_region'),
      }),
    );
  };

  deleteRegion = (id: RegionId) => {
    return showDeleteConfirmation('Delete Term', `Do you really want to remove this term?`).pipe(
      switchMap(() => RegionApi.deleteRegion(id)),
      this.tmRegionsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_region'),
      }),
    );
  };

  saveProviderPriorities = (data: CategoryRegionProviderPriorityForm) => {
    return RegionApi.saveProviderPriorities(data).pipe(
      handleMessage({
        type: requestMessage('update_region_priority'),
      }),
    );
  };
}
